import React, { useCallback, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, withRouter } from "react-router-dom";
import { CardContent, Grid, IconButton, List, ListItem, ListItemText, TextField, Typography } from "@material-ui/core";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { CardPageItem } from "../../../custom/Page";
import SelectUserSituations from "../select/SelectUserSituations";
import { debounce, filterOnlyNumbers, HierarchyViewTypeEnum, Roles } from "../../../util";
import useRemoveArrowsNumberInput from "../../../util/useRemoveArrowsNumberInput";
import { getUsersBelow } from "../../../actions";
import InfiniteScrollAsync from "../InfiniteScrollAsync";
import AbtcAvatar from "../../../custom/Avatar";
import HierarchySelect from "../select/HierarchySelect";
import SelectLocation from "../select/SelectLocation";
import SelectWithUserRoles from "../select/SelectWithUserRoles";

const Search = ({ location }) => {
  const classes = useRemoveArrowsNumberInput();

  const locationState = location?.state;
  const searchParams = new URLSearchParams(location?.search);

  const customerSituationParam = searchParams?.get("customerSituation");
  const agentSituationParam = searchParams?.get("agentSituation");
  const roleParam = searchParams?.get("role");

  const [filter, setFilter] = useState({
    agentSituationsSelected: agentSituationParam ? [agentSituationParam] : locationState ? locationState.agentSituationsSelected : [],
    customerSituationsSelected: customerSituationParam ? [customerSituationParam] : locationState ? locationState.customerSituationsSelected : [],
    searchById: locationState ? locationState.searchById : "",
    searchByText: locationState ? locationState.searchByText : "",
    selectedCountries: locationState ? locationState.selectedCountries : [],
    selectedHierarchyViewType: locationState ? locationState.selectedHierarchyViewType : HierarchyViewTypeEnum.ALL,
    selectedUserRoles: roleParam ? [roleParam] : locationState ? locationState.selectedUserRoles : [],
    selectedStates: locationState ? locationState.selectedStates : [],
  });

  const filterRef = useRef(filter);
  filterRef.current = filter;

  const handleSearchText = debounce((value) => {
    setFilter((prev) => ({ ...prev, searchById: "", searchByText: value }));
  });

  const handleSearchId = debounce((value) => {
    setFilter((prev) => ({ ...prev, searchByText: "", searchById: value }));
  });

  const updateFilter = useCallback((key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  }, []);

  const hasFilters = React.useMemo(() => {
    return (
      filter.agentSituationsSelected.length > 0 ||
      filter.customerSituationsSelected.length > 0 ||
      filter.searchById.length > 0 ||
      filter.searchByText.length > 0 ||
      filter.selectedCountries.length > 0 ||
      filter.selectedUserRoles.length > 0 ||
      filter.selectedStates.length > 0
    );
  }, [filter.agentSituationsSelected, filter.customerSituationsSelected, filter.searchById, filter.searchByText, filter.selectedCountries, filter.selectedUserRoles, filter.selectedStates]);

  const paramsMemo = React.useMemo(() => {
    if (!hasFilters) return null;
    return {
      hierarchyViewType: filter.selectedHierarchyViewType,
      ...(filter.selectedUserRoles?.length && {
        role: filter.selectedUserRoles,
      }),
      ...(filter.selectedCountries && { country: filter.selectedCountries }),
      ...(filter.customerSituationsSelected?.length && {
        customerSituation: filter.customerSituationsSelected,
      }),
      ...(filter.agentSituationsSelected?.length && {
        agentSituation: filter.agentSituationsSelected,
      }),
      ...(filter.selectedStates?.length && { state: filter.selectedStates }),
      ...(filter.searchById?.length && { userId: filter.searchById }),
      ...(filter.searchByText?.length && { nameOrEmail: filter.searchByText }),
    };
  }, [filter, hasFilters]);

  const fetchUsersPaginate = async ({ params, skip = 0, take = 10 }) => {
    if (!params) return [];
    const paramsWithPaging = { ...params, skip, take };
    const users = await getUsersBelow(paramsWithPaging);
    return users;
  };

  return (
    <>
      <CardPageItem
        raised
        margin={1}
      >
        <CardContent>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={8}
              sm={8}
              md={8}
            >
              <TextField
                id="nameSearchField"
                variant="outlined"
                size="small"
                defaultValue={filter.searchByText}
                fullWidth
                onChange={(e) => handleSearchText(e.target.value)}
                label={<FormattedMessage id="app.search.nameemail" />}
              />
            </Grid>
            <Grid
              item
              xs={4}
              sm={4}
              md={4}
            >
              <TextField
                id="idField"
                variant="outlined"
                size="small"
                defaultValue={filter.searchById}
                fullWidth
                type="number"
                className={classes.input}
                onInput={filterOnlyNumbers}
                onChange={(e) => handleSearchId(e.target.value)}
                label={<FormattedMessage id="app.search.id" />}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
            >
              <HierarchySelect
                value={filter.selectedHierarchyViewType}
                defaultSelected={HierarchyViewTypeEnum.ALL}
                onChange={(value) => updateFilter("selectedHierarchyViewType", value)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
            >
              <SelectWithUserRoles
                selectedUserRoles={filter.selectedUserRoles}
                setSelectedUserRoles={(value) => updateFilter("selectedUserRoles", value)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
            >
              <SelectUserSituations
                title="app.situation.customer"
                selectedUserSituations={filter.customerSituationsSelected}
                setSelectedUserSituations={(value) => updateFilter("customerSituationsSelected", value)}
                enableInapt
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
            >
              <SelectUserSituations
                title="app.situation.agent"
                selectedUserSituations={filter.agentSituationsSelected}
                setSelectedUserSituations={(value) => updateFilter("agentSituationsSelected", value)}
              />
            </Grid>
            <SelectLocation
              fetchStatesEnabled
              selectedCountries={filter.selectedCountries}
              setSelectedCountries={(value) => updateFilter("selectedCountries", value)}
              selectedStates={filter.selectedStates}
              setSelectedStates={(value) => updateFilter("selectedStates", value)}
            />
          </Grid>
        </CardContent>
      </CardPageItem>

      <CardPageItem
        raised
        margin={1}
      >
        <CardContent>
          {!hasFilters ? (
            <Typography
              variant="body1"
              align="center"
            >
              <FormattedMessage id="app.users.apply.filter" />
            </Typography>
          ) : (
            <>
              <List>
                <InfiniteScrollAsync
                  emptyMessage={"app.users.not.filter"}
                  offsetIncrement={50}
                  onScroll={fetchUsersPaginate}
                  params={paramsMemo}
                  render={(user) => (
                    <UserListItem
                      key={user.id}
                      user={user}
                      filter={filterRef}
                    />
                  )}
                  take={20}
                />
              </List>
            </>
          )}
        </CardContent>
      </CardPageItem>
    </>
  );
};

const UserListItem = ({ user, filter }) => {
  return (
    <ListItem
      key={user.id}
      button
      component={Link}
      disableGutters
      to={{
        pathname: `/user-detail/${user.id}`,
        state: { backTo: "/team-management/search", filter: filter.current },
      }}
    >
      <Grid
        container
        alignItems="center"
        spacing={1}
      >
        <Grid
          item
          xs={2}
          md={1}
          align="center"
        >
          <AbtcAvatar
            imageUrl={user.photoUrl}
            enablePremiumBorder={user.premium}
          />
        </Grid>
        <Grid
          item
          xs={5}
          md={6}
        >
          <ListItemText
            primary={
              <>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  component={"span"}
                >
                  {user?.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  component={"span"}
                >
                  {` (${user.id})`}
                </Typography>
              </>
            }
            secondary={
              <>
                <Typography
                  color="textSecondary"
                  component={"span"}
                  variant="caption"
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.email}
                  <br />
                  {user?.agentName}
                  {user.premium && (
                    <>
                      <br />
                      <Typography
                        variant="caption"
                        color="primary"
                        component={"span"}
                      >
                        Premium
                      </Typography>
                    </>
                  )}
                </Typography>
              </>
            }
          />
        </Grid>

        <Grid
          item
          xs={4}
        >
          <ListItemText
            primary={
              <Typography
                variant="body1"
                color="textPrimary"
              >
                {user.role === Roles.Agent ? <FormattedMessage id={`app.enuns.levelName.${user.levelName}`} /> : <FormattedMessage id={`app.enuns.${user.role}`} />}
              </Typography>
            }
            secondary={
              <>
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  {user?.customerSituation && (
                    <>
                      <FormattedMessage id="app.customer" />
                      <span>{`: `}</span>
                      <FormattedMessage id={`app.user.situation.${user.customerSituation}`} />
                    </>
                  )}
                  {user?.agentSituation && (
                    <>
                      <br />
                      <FormattedMessage id="app.agent" />
                      <span>{`: `}</span>
                      <FormattedMessage id={`app.user.situation.${user.agentSituation}`} />
                    </>
                  )}
                </Typography>
                <br />
                <Typography
                  variant="caption"
                  color="textSecondary"
                >
                  {user?.country && (
                    <FormattedMessage
                      id={`app.taxResidence.${user.country}`}
                      defaultMessage={user.country}
                    />
                  )}
                  {user?.state && ` - ${user.state}`}
                </Typography>
              </>
            }
          />
        </Grid>

        <Grid
          item
          xs={1}
        >
          <IconButton
            color="primary"
            edge={"start"}
          >
            <ChevronRight />
          </IconButton>
        </Grid>
      </Grid>
    </ListItem>
  );
};

export default withRouter(Search);
